<script setup>
import { useField } from 'vee-validate'
import { useTemplateRef, watch } from 'vue'

import ImoSpinner from '@/components/loading/ImoSpinner.vue'
import { useFormatTools } from '@/composables/useFormatTools'
import { useSearchDropdown } from '@/composables/useSearchDropdown'
import Clients from '@/services/models/Clients.js'

const { formatDocument } = useFormatTools()

const emit = defineEmits(['input', 'selectData'])

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: 'Cliente:',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  partner: {
    type: Boolean,
    default: false,
  },
})

const inputRef = useTemplateRef('inputRef')
const dropdownRef = useTemplateRef('dropdownRef')
const ulRef = useTemplateRef('ulRef')

const model = defineModel({ type: String })
const documentModel = defineModel('document', { type: String })

const { value, errorMessage, handleReset } = useField(() => props.name, undefined, {
  syncVModel: true,
  validateOnValueUpdate: false,
})

// Hook do dropdown
const {
  searchQuery,
  results,
  isDropdownVisible,
  isLoading,
  activeIndex,
  isKeyboardNavigation,
  openDropdown,
  closeDropdown,
  selectResult,
  cleanAll,
  handleKeyDown,
  handleScroll,
  handleMouseEnterDropdown,
  handleMouseLeaveDropdown,
  handleMouseEnterItem,
} = useSearchDropdown({
  fetchService: url => Clients.list(url),
  readService: id => Clients.read(id),
  buildSearchUrl: searchValue =>
    props.partner ? `?search=${searchValue}&is_partner=true` : `?search=${searchValue}`,
  onSelect: item => {
    handleReset()
    emit('selectData', item)
    value.value = item.id
    documentModel.value = item.document
    searchQuery.value = item.name
  },
  inputRef,
  dropdownRef,
  ulRef,
  initialValue: model,
})

// Função para limpar tudo incluindo vee-validate
const handleCleanAll = () => {
  value.value = ''
  handleReset()
  cleanAll()
}

// Override da função openDropdown para incluir reset de erros
const handleOpenDropdown = () => {
  if (errorMessage.value) {
    handleReset()
  }
  openDropdown()
}

// Sincronizar v-model externo com searchQuery
watch(model, newValue => {
  if (!newValue) {
    searchQuery.value = ''
  }
})
</script>

<template>
  <div class="relative flex flex-col gap-1">
    <label
      class="text-neutral-60"
      :class="{
        'text-base': size === 'lg',
        'text-sm': size === 'md',
        'text-xs': size === 'sm',
      }"
      :for="$attrs.id"
    >
      {{ label }}
    </label>
    <div class="relative w-full">
      <input
        class="placeholder:text-neutral-40 w-full rounded-lg pr-16"
        :class="{
          error: errorMessage || (!results.length && !isLoading),
          input: !errorMessage && (results.length || isLoading),
          'h-[38px]': size === 'md',
          'h-[44px]': size === 'lg',
          'h-[36px]': size === 'sm',
          'border-neutral-30': !value,
          'border-neutral-40': value,
        }"
        id="search-input"
        type="text"
        v-model="searchQuery"
        @focus="handleOpenDropdown"
        @blur="closeDropdown"
        @keydown="handleKeyDown"
        placeholder="Digite para buscar..."
        ref="inputRef"
        autocomplete="off"
      />

      <!-- Spinner quando está carregando -->
      <ImoSpinner
        v-if="isLoading && searchQuery"
        class="absolute top-1/2 right-3 -translate-y-1/2"
        :class="{
          'text-error-60': errorMessage || !results.length,
          'text-primary-60': !errorMessage && results.length,
        }"
        variant="primary"
        :size="20"
      />

      <!-- Ícone de limpar (X) quando tem texto e não está carregando -->
      <ImoIcon
        v-else-if="searchQuery"
        @click="handleCleanAll"
        class="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer"
        :class="{
          'text-error-60': errorMessage || (!results.length && !isLoading),
          'text-gray-400 hover:text-gray-600': !errorMessage && (results.length || isLoading),
        }"
        name="close"
      />

      <!-- Ícone de seta (arrow-down) quando não há texto -->
      <ImoIcon
        v-else
        class="text-neutral-40 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 transition-transform duration-200"
        :class="{
          'rotate-180': isDropdownVisible,
        }"
        name="arrow-down-02"
      />
    </div>
    <input id="client-input-id" type="text" v-model="value" class="hidden" />

    <!-- Dropdown -->
    <div
      v-if="isDropdownVisible"
      ref="dropdownRef"
      class="border-neutral-20 absolute top-17 z-40 w-full rounded-lg border bg-white p-1 shadow-lg"
      @mouseenter="handleMouseEnterDropdown"
      @mouseleave="handleMouseLeaveDropdown"
    >
      <ul
        v-if="results.length"
        ref="ulRef"
        class="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-20 max-h-72 overflow-y-auto"
        @scroll="handleScroll"
      >
        <li
          v-for="(item, index) in results"
          :key="index"
          @mousedown="selectResult(item)"
          @mouseenter="handleMouseEnterItem(index)"
          class="flex cursor-pointer flex-col gap-0.5 rounded-lg py-2 pr-5 pl-4"
          :class="{
            'bg-neutral-20': index === activeIndex,
            'hover:bg-neutral-20': index !== activeIndex && !isKeyboardNavigation,
          }"
        >
          <span class="text-neutral-90 text-sm font-normal">{{ item.name }} </span>
          <span class="text-neutral-60 text-xs font-normal">
            {{ formatDocument(item.document) }}
          </span>
        </li>

        <!-- Spinner no final da lista quando está carregando mais -->
        <li v-if="isLoading" class="flex justify-center py-3">
          <ImoSpinner variant="primary" :size="24" />
        </li>
      </ul>
      <div v-else-if="!isLoading" class="flex flex-col gap-6 rounded-lg py-2 pr-5 pl-4">
        <span class="text-neutral-90 text-center text-sm font-normal">
          Correntista não encontrado. Verifique os dados digitados.
        </span>
      </div>
    </div>

    <p
      v-if="!results.length && !isLoading && !isDropdownVisible && !errorMessage"
      class="text-error-60 text-xs"
    >
      Não localizado. Verifique os dados digitados.
    </p>

    <span class="text-error-60 text-xs" :id="`contact-${$attrs.id}-errors`">
      {{ errorMessage }}
    </span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
